import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import { useTheme } from "../hooks/useTheme";
import type { Kid } from "@starquest/core";
import styles from "./GiftStars.module.css";

const REASON_TEMPLATES = [
  { emoji: "💖", label: "Acts of kindness" },
  { emoji: "🎓", label: "Great grades" },
  { emoji: "🤝", label: "Helped a sibling" },
  { emoji: "🦁", label: "Brave moment" },
  { emoji: "✨", label: "Just because" },
  { emoji: "🎂", label: "Birthday bonus" },
];

const QUICK_AMOUNTS = [5, 10, 15, 25, 50, 100];

export default function GiftStars() {
  const navigate  = useNavigate();
  const { familyId } = useAuthStore();
  const { kids, setKids } = useFamilyStore();
  const theme = useTheme();

  const [selectedKid,    setSelectedKid]    = useState<string>("");
  const [amount,         setAmount]         = useState(25);
  const [reason,         setReason]         = useState("");
  const [customReason,   setCustomReason]   = useState("");
  const [sent,           setSent]           = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState("");

  useEffect(() => {
    if (!familyId) return;
    api.listKids(familyId).then((k) => { const list = k as Kid[]; setKids(list); if (list.length) setSelectedKid(list[0].kidId); });
  }, [familyId]);

  async function handleSend() {
    if (!familyId || !selectedKid) return;
    const finalReason = customReason.trim() || reason;
    if (!finalReason) { setError("Please pick a reason or write one"); return; }
    setError(""); setLoading(true);
    try {
      await api.sendBonus(familyId, { kidId: selectedKid, amount, reason: finalReason });
      setSent(true);
      api.listKids(familyId).then((k) => setKids(k as Kid[]));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to send bonus");
    } finally { setLoading(false); }
  }

  const kid = kids.find((k) => k.kidId === selectedKid);

  if (sent) return (
    <div className={styles.page}>
      <ParentSidebar />
      <main className={styles.main}>
        <div className={styles.toast}>
          <div className={styles.toastBig}>✨</div>
          <div className={styles.toastTitle}>Sent {theme.pointEmoji} {amount} to {kid?.name}!</div>
          <p className={styles.toastSub}>The {theme.pointNamePlural.toLowerCase()} are in their {theme.pointBankTitle.toLowerCase()}.</p>
          <button className={styles.toastBtn} onClick={() => { setSent(false); setAmount(25); setReason(""); setCustomReason(""); navigate("/parent"); }}>
            Back to Dashboard
          </button>
        </div>
      </main>
      <BottomNav />
    </div>
  );

  return (
    <div className={styles.page}>
      <ParentSidebar />
      <main className={styles.main}>
        <div className={styles.head}>
          <h1 className={styles.h1}>🎁 Gift {theme.pointNamePlural}</h1>
          <div className={styles.sub}>Grant bonus {theme.pointNamePlural.toLowerCase()} for acts of kindness, achievements, or just because.</div>
        </div>

        <div className={styles.grid}>
          <div className={styles.composer}>
            {/* Step 1: Pick explorer */}
            <div className={styles.section}>
              <div className={styles.stepLabel}><span className={styles.stepNum}>1</span>Who gets the {theme.pointNamePlural.toLowerCase()}?</div>
              <div className={styles.expPicker}>
                {kids.map((k) => (
                  <button
                    key={k.kidId}
                    className={`${styles.expChip} ${selectedKid === k.kidId ? styles.expActive : ""}`}
                    onClick={() => setSelectedKid(k.kidId)}
                  >
                    <div className={styles.expAvi}>{k.avatar}</div>
                    <div>
                      <div className={styles.expName}>{k.name}</div>
                      <div className={styles.expBal}>{theme.pointEmoji} {k.pointBalance}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Amount */}
            <div className={styles.section}>
              <div className={styles.stepLabel}><span className={styles.stepNum}>2</span>How many {theme.pointNamePlural.toLowerCase()}?</div>
              <div className={styles.amtWrap}>
                <div className={styles.amtBig}>
                  <span className={styles.amtSym}>{theme.pointEmoji}</span>
                  <input type="number" value={amount} min={1}
                    onChange={(e) => setAmount(+e.target.value || 1)} />
                </div>
                <div className={styles.amtSteps}>
                  <button onClick={() => setAmount((a) => a + 5)}>+</button>
                  <button onClick={() => setAmount((a) => Math.max(1, a - 5))}>−</button>
                </div>
              </div>
              <div className={styles.quickAmts}>
                {QUICK_AMOUNTS.map((q) => (
                  <button key={q} className={`${styles.quick} ${amount === q ? styles.quickActive : ""}`}
                    onClick={() => setAmount(q)}>{theme.pointEmoji}{q}</button>
                ))}
              </div>
            </div>

            {/* Step 3: Reason */}
            <div className={styles.section}>
              <div className={styles.stepLabel}><span className={styles.stepNum}>3</span>Why?</div>
              <div className={styles.reasonGrid}>
                {REASON_TEMPLATES.map((r) => (
                  <button key={r.label}
                    className={`${styles.reasonBtn} ${reason === r.label ? styles.reasonActive : ""}`}
                    onClick={() => { setReason(r.label); setCustomReason(""); }}
                  >
                    <span className={styles.reasonEmoji}>{r.emoji}</span>
                    <span>{r.label}</span>
                  </button>
                ))}
              </div>
              <textarea
                className={styles.customReason}
                placeholder="Or write your own reason…"
                value={customReason}
                onChange={(e) => { setCustomReason(e.target.value); setReason(""); }}
              />
            </div>

            {/* Preview */}
            {kid && (
              <div className={styles.preview}>
                <div className={styles.previewIcon}>✨</div>
                <div>
                  <div>{kid.name} will receive a sparkle notification:</div>
                  <div className={styles.previewMsg}>
                    {(customReason || reason)
                      ? `"${customReason || reason}" — ${theme.pointEmoji} ${amount} bonus from ${theme.parentRole}`
                      : <span style={{ color: "rgba(240,240,255,0.4)", fontStyle: "italic" }}>Select a reason above to preview the message</span>
                    }
                  </div>
                </div>
              </div>
            )}

            {error && <p className={styles.error}>{error}</p>}

            <button className={styles.sendBtn} onClick={handleSend} disabled={loading || !selectedKid}>
              {loading ? "Sending…" : <><span className={styles.sparkle}>✨</span> Send Bonus {theme.pointNamePlural} <span className={styles.sparkle}>✨</span></>}
            </button>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
